import { httpsCallable } from 'firebase/functions';
import { useState } from 'react';
import type { ExportParticipantsInput, ExportParticipantsResult } from '@citizen-walk/shared-types';
import { functions } from '../../lib/firebase';

/**
 * exportParticipants callable hook — 参加者 (一般ユーザー) 登録情報の CSV / JSON 一括出力。
 *
 * useExportPosts と同 pattern (idle / pending / success / error 4 状態)。
 * download 起動は本 hook の責務外 (page 側で Blob 生成 + a.click、`triggerParticipantsDownload`)。
 */

export type UseExportParticipantsState =
  | { status: 'idle'; result: null; error: null }
  | { status: 'pending'; result: null; error: null }
  | { status: 'success'; result: ExportParticipantsResult; error: null }
  | { status: 'error'; result: null; error: Error };

export interface UseExportParticipantsReturn {
  state: UseExportParticipantsState;
  call: (input: ExportParticipantsInput) => Promise<ExportParticipantsResult | null>;
  reset: () => void;
}

export function useExportParticipants(): UseExportParticipantsReturn {
  const [state, setState] = useState<UseExportParticipantsState>({
    status: 'idle',
    result: null,
    error: null,
  });

  async function call(input: ExportParticipantsInput): Promise<ExportParticipantsResult | null> {
    setState({ status: 'pending', result: null, error: null });
    try {
      const fn = httpsCallable<ExportParticipantsInput, ExportParticipantsResult>(
        functions,
        'exportParticipants',
      );
      const res = await fn(input);
      setState({ status: 'success', result: res.data, error: null });
      return res.data;
    } catch (e) {
      const err = e as Error;
      setState({ status: 'error', result: null, error: err });
      return null;
    }
  }

  function reset() {
    setState({ status: 'idle', result: null, error: null });
  }

  return { state, call, reset };
}

/**
 * CSV / JSON content をブラウザ download として起動する helper。
 * CSV のみ UTF-8 BOM を先頭付加 (Excel での文字化け回避、useExportPosts と同 logic)。
 */
export function triggerParticipantsDownload(result: ExportParticipantsResult): void {
  const mime =
    result.format === 'csv' ? 'text/csv;charset=utf-8' : 'application/json;charset=utf-8';
  const parts = result.format === 'csv' ? ['﻿', result.content] : [result.content];
  const blob = new Blob(parts, { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = result.filename;
  a.click();
  URL.revokeObjectURL(url);
}
